import React, { useState } from 'react';
import { 
  X, Instagram, Facebook, Linkedin, Youtube, 
  Target, DollarSign, Megaphone, Check, Plus
} from 'lucide-react';
import { 
  Dialog, DialogContent, DialogHeader, 
  DialogTitle, DialogDescription, DialogFooter 
} from "@/ui/dialog";
import { Button } from '@/ui/button';
import { Input } from '@/ui/input';
import { Label } from '@/ui/label';
import API from '@/api/api01';
import { toast } from 'sonner';

interface CreateCampaignModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export default function CreateCampaignModal({ open, onOpenChange, onSuccess }: CreateCampaignModalProps) {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [platform, setPlatform] = useState('Facebook');
  const [targetAudience, setTargetAudience] = useState('');
  const [budget, setBudget] = useState('');

  const handleCreate = async () => {
    if (!name) {
      toast.error("Campaign name is required");
      return;
    }

    try {
      setLoading(true);
      await API.post('/superadmin/social-campaigns', {
        name,
        platform,
        target_audience: targetAudience,
        budget: parseInt(budget) || 0,
        status: 'Active'
      });
      toast.success("Campaign created successfully!");
      onSuccess();
      onOpenChange(false);
      resetForm();
    } catch (err) {
      toast.error("Failed to create campaign");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setPlatform('Facebook');
    setTargetAudience('');
    setBudget('');
  };

  const PLATFORMS = [
    { id: 'Facebook', icon: <Facebook className="text-[#1877F2]" /> },
    { id: 'Instagram', icon: <Instagram className="text-[#E1306C]" /> },
    { id: 'LinkedIn', icon: <Linkedin className="text-[#0A66C2]" /> },
    { id: 'YouTube', icon: <Youtube className="text-[#FF0000]" /> },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) resetForm(); }}>
      <DialogContent className="sm:max-w-[500px] w-[95vw] rounded-[2.5rem] border-none shadow-2xl p-0 overflow-hidden bg-white dark:bg-slate-950 [&>button]:hidden">
        {/* Animated Header */}
        <div className="bg-slate-900 p-8 text-white relative overflow-hidden shrink-0">
          <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
            <div className="absolute top-[-50%] left-[-20%] w-[140%] h-[140%] bg-[radial-gradient(circle,rgba(99,102,241,0.4)_0%,transparent_70%)]" />
          </div>

          <DialogHeader className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="h-12 w-12 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 shadow-xl">
                <Megaphone size={24} className="text-indigo-400" />
              </div>
              <button onClick={() => onOpenChange(false)} className="h-10 w-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors">
                <X size={20} className="text-white" />
              </button>
            </div>
            <DialogTitle className="text-2xl font-bold tracking-tight leading-tight">Create Campaign</DialogTitle>
            <DialogDescription className="text-white/50 mt-1 font-medium text-sm">Register a new social media campaign for lead tracking.</DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-8 space-y-6">
          <div className="space-y-3">
            <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Campaign Name</Label>
            <Input 
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Summer Sales 2024"
              className="h-12 rounded-xl bg-slate-50 dark:bg-slate-900 border-none shadow-sm focus:ring-2 focus:ring-indigo-500 font-semibold"
            />
          </div>

          <div className="space-y-3">
            <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Target Platform</Label>
            <div className="grid grid-cols-4 gap-3">
              {PLATFORMS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPlatform(p.id)}
                  className={cn(
                    "flex flex-col items-center justify-center gap-2 p-3 rounded-2xl border-2 transition-all duration-300",
                    platform === p.id 
                      ? "bg-indigo-500/10 border-indigo-500 scale-105 shadow-md" 
                      : "bg-white dark:bg-slate-900 border-slate-100 dark:border-white/5 hover:border-indigo-200"
                  )}
                >
                  {p.icon}
                  <span className={cn("text-[9px] font-bold uppercase tracking-widest", platform === p.id ? "text-indigo-600" : "text-slate-400")}>
                    {p.id}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Budget ($)</Label>
              <div className="relative">
                <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input 
                  type="number"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  placeholder="0.00"
                  className="h-12 pl-9 rounded-xl bg-slate-50 dark:bg-slate-900 border-none shadow-sm focus:ring-2 focus:ring-indigo-500 font-semibold"
                />
              </div>
            </div>
            <div className="space-y-3">
              <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Target Audience</Label>
              <div className="relative">
                <Target size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input 
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  placeholder="e.g. Students"
                  className="h-12 pl-9 rounded-xl bg-slate-50 dark:bg-slate-900 border-none shadow-sm focus:ring-2 focus:ring-indigo-500 font-semibold"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="p-8 pt-0 flex gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1 rounded-2xl h-12 text-xs font-black uppercase tracking-widest border-slate-100 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5">
            Cancel
          </Button>
          <Button 
            disabled={loading}
            onClick={handleCreate}
            className="flex-1 rounded-2xl h-12 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black uppercase tracking-widest shadow-xl shadow-indigo-500/20"
          >
            {loading ? "Creating..." : "Launch Campaign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Helper to handle class merging
function cn(...classes: any[]) {
  return classes.filter(Boolean).join(' ');
}
